"use client";

import Link from "next/link";
import React, { useEffect, useState } from "react";
import { captureProductClientEvent } from "@/lib/analytics/product-client-event";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { detectInAppBrowser, type InAppBrowserDetection } from "@/lib/in-app-browser";
import InAppBrowserNotice from "./InAppBrowserNotice";
import SlackSvg from "@/components/Icons/SlackSvg";

import { Button } from "@/components/ui/button";

interface SocialOAuthButtonsProps {
  googleUrl: string;
  /** Optional so a page can offer Google alone; the Microsoft button renders only when supplied. */
  microsoftUrl?: string;
  /** Optional like Microsoft; the Slack button renders only when supplied. */
  slackUrl?: string;
  /** Button copy prefix — "Continue with" (login, default) or "Sign up with" (signup). */
  labelPrefix?: string;
}

export default function SocialOAuthButtons({ googleUrl, microsoftUrl, slackUrl, labelPrefix = "Continue with" }: SocialOAuthButtonsProps) {
  // The UA is only available client-side. Detect after mount so the server
  // render and the first client render agree (the OAuth buttons), then swap to
  // the notice if we're inside an in-app browser. Guarding on `null` (rather
  // than branching on `navigator` during render) avoids a hydration mismatch.
  const [detection, setDetection] = useState<InAppBrowserDetection | null>(null);
  useEffect(() => {
    setDetection(detectInAppBrowser());
  }, []);

  if (detection?.isInApp) {
    return <InAppBrowserNotice detection={detection} />;
  }

  return (
    <>
      <Button asChild variant="outline" className="relative flex w-full">
        <Link href={googleUrl} data-testid="oauth-google-button" onClick={() => captureProductClientEvent(ANALYTICS_EVENTS.AUTH_GOOGLE_OAUTH_CLICKED)}>
          <svg className="absolute left-4" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span className="flex-1 text-center">{labelPrefix} Google</span>
        </Link>
      </Button>

      {microsoftUrl && (
        <Button asChild variant="outline" className="relative flex w-full">
          <Link href={microsoftUrl} data-testid="oauth-microsoft-button" onClick={() => captureProductClientEvent(ANALYTICS_EVENTS.AUTH_MICROSOFT_OAUTH_CLICKED)}>
            <svg className="absolute left-4" viewBox="0 0 24 24">
              <path fill="#F25022" d="M1 1h10v10H1z"/>
              <path fill="#00A4EF" d="M1 13h10v10H1z"/>
              <path fill="#7FBA00" d="M13 1h10v10H13z"/>
              <path fill="#FFB900" d="M13 13h10v10H13z"/>
            </svg>
            <span className="flex-1 text-center">{labelPrefix} Microsoft</span>
          </Link>
        </Button>
      )}

      {slackUrl && (
        <Button asChild variant="outline" className="relative flex w-full">
          <Link href={slackUrl} data-testid="oauth-slack-button" onClick={() => captureProductClientEvent(ANALYTICS_EVENTS.AUTH_SLACK_OAUTH_CLICKED)}>
            <SlackSvg size={16} className="absolute left-4" />
            <span className="flex-1 text-center">{labelPrefix} Slack</span>
          </Link>
        </Button>
      )}
    </>
  );
}
